import { useCallback, useEffect, useState } from 'react';
import api, { ApiError } from '../../api/client.js';
import { ROLE_LABELS } from '../../lib/constants.js';
import ConfirmDialog from '../../components/ConfirmDialog.jsx';

const BLANK_USER = { username: '', password: '', displayName: '', role: 'staff' };

export default function UsersPanel({ currentUser, showToast }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(BLANK_USER);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.getUsers();
      setUsers(data.users);
    } catch (caught) {
      showToast(caught?.message || 'تعذّر تحميل المستخدمين', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(event) {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setErrors({});
    setFormError('');

    try {
      await api.createUser(form);
      setForm(BLANK_USER);
      setCreating(false);
      await load();
      showToast('تم إنشاء الحساب');
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFormError(caught.message);
        if (caught.details) setErrors(caught.details);
      } else {
        setFormError('تعذّر إنشاء الحساب');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(user) {
    try {
      await api.updateUser(user.id, { isActive: !user.isActive });
      await load();
      showToast(user.isActive ? 'تم إلغاء تفعيل الحساب' : 'تم تفعيل الحساب');
    } catch (caught) {
      showToast(caught?.message || 'تعذّر تحديث الحساب', 'error');
    }
  }

  async function handleResetPassword(event) {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      await api.updateUser(resetTarget.id, { password: newPassword });
      setResetTarget(null);
      setNewPassword('');
      showToast('تم تغيير كلمة المرور. سيُطلب من المستخدم الدخول من جديد.');
    } catch (caught) {
      showToast(caught?.message || 'تعذّر تغيير كلمة المرور', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    try {
      await api.deleteUser(pendingDelete.id);
      setPendingDelete(null);
      await load();
      showToast('تم حذف الحساب');
    } catch (caught) {
      showToast(caught?.message || 'تعذّر حذف الحساب', 'error');
    }
  }

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>حسابات المستخدمين</h2>
          <p>
            «مدير النظام» يدير كل شيء، «موظف المكتب» يدير المواعيد، و«عرض فقط» للقراءة
            والطباعة — وهذه الصلاحيات مطبّقة على الخادم وليست إخفاءً للأزرار.
          </p>
        </div>
        <button type="button" className="btn btn-gold" onClick={() => setCreating(true)}>
          ＋ حساب جديد
        </button>
      </div>

      <div className="panel-body">
        {loading ? (
          <div className="loading-state">...جاري التحميل</div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>اسم المستخدم</th>
                  <th>الاسم الظاهر</th>
                  <th>الدور</th>
                  <th>الحالة</th>
                  <th>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td dir="ltr" style={{ textAlign: 'right' }}>
                      {user.username}
                    </td>
                    <td>{user.displayName}</td>
                    <td>{ROLE_LABELS[user.role] || user.role}</td>
                    <td>
                      {user.isActive ? (
                        <span className="badge badge-status">مفعّل</span>
                      ) : (
                        <span className="badge badge-status" data-status="ملغى">
                          غير مفعّل
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="row">
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setResetTarget(user)}
                        >
                          كلمة المرور
                        </button>

                        {/* An admin cannot deactivate or delete their own
                            account, which would lock them out mid-session. */}
                        {user.id !== currentUser.id && (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => toggleActive(user)}
                            >
                              {user.isActive ? 'إلغاء التفعيل' : 'تفعيل'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => setPendingDelete(user)}
                            >
                              حذف
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && (
        <div
          className="modal-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setCreating(false);
          }}
        >
          <div className="modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>حساب جديد</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setCreating(false)}
                aria-label="إغلاق"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} noValidate>
              <div className="modal-body">
                {formError && <div className="form-error">{formError}</div>}

                <div className="field">
                  <label htmlFor="new-username">اسم المستخدم</label>
                  <input
                    id="new-username"
                    dir="ltr"
                    value={form.username}
                    onChange={(event) =>
                      setForm({ ...form, username: event.target.value })
                    }
                    autoCapitalize="none"
                    spellCheck="false"
                    required
                  />
                  {errors.username && <div className="field-error">{errors.username}</div>}
                  <p className="hint">أحرف لاتينية وأرقام فقط، 3 أحرف على الأقل.</p>
                </div>

                <div className="field">
                  <label htmlFor="new-display-name">الاسم الظاهر</label>
                  <input
                    id="new-display-name"
                    value={form.displayName}
                    onChange={(event) =>
                      setForm({ ...form, displayName: event.target.value })
                    }
                    required
                  />
                  {errors.displayName && (
                    <div className="field-error">{errors.displayName}</div>
                  )}
                </div>

                <div className="field">
                  <label htmlFor="new-role">الدور</label>
                  <select
                    id="new-role"
                    value={form.role}
                    onChange={(event) => setForm({ ...form, role: event.target.value })}
                  >
                    <option value="staff">موظف المكتب</option>
                    <option value="viewer">عرض فقط</option>
                    <option value="admin">مدير النظام</option>
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="new-password">كلمة المرور</label>
                  <input
                    id="new-password"
                    type="password"
                    dir="ltr"
                    value={form.password}
                    onChange={(event) =>
                      setForm({ ...form, password: event.target.value })
                    }
                    required
                  />
                  {errors.password && <div className="field-error">{errors.password}</div>}
                  <p className="hint">10 أحرف على الأقل.</p>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setCreating(false)}>
                  إلغاء
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? '...جاري الحفظ' : 'إنشاء'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetTarget && (
        <div
          className="modal-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setResetTarget(null);
          }}
        >
          <div className="modal" style={{ maxWidth: '430px' }} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>تغيير كلمة المرور</h2>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="modal-body">
                <p style={{ marginTop: 0 }}>
                  الحساب: <strong>{resetTarget.displayName}</strong>
                </p>
                <div className="field">
                  <label htmlFor="reset-password">كلمة المرور الجديدة</label>
                  <input
                    id="reset-password"
                    type="password"
                    dir="ltr"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    required
                    autoFocus
                  />
                  <p className="hint">
                    ستنتهي جميع جلسات هذا المستخدم فور التغيير.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setResetTarget(null);
                    setNewPassword('');
                  }}
                >
                  إلغاء
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  حفظ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="حذف الحساب"
          message={`هل تريد حذف حساب «${pendingDelete.displayName}»؟`}
          detail="ستبقى المواعيد التي أنشأها الحساب موجودة. إذا كنت تريد منع الدخول مؤقتاً فقط، فالأفضل إلغاء التفعيل بدلاً من الحذف."
          confirmLabel="حذف"
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </section>
  );
}
